// ============================================================
// teamRolesDb.js
// Firestore I/O for the `teamRoles` collection — the editable half of the role
// system. All of the RULES live in data/teamMembers.js, which is pure; this
// module only reads and writes.
//
// ── WHY A NEW COLLECTION, NOT `users` ──
// The `users` collection already exists and holds stale seed data on
// @atlanticfence.com — the wrong domain for these accounts — written by the
// seeding code still preserved (commented out) in RoleContext.js. Writing real
// records beside them would mean a lookup could match a stale one, and that
// seeder repopulates `users` whenever it is empty if anyone ever restores it.
// `teamRoles` starts genuinely empty, and empty means "use the file", which is
// the safe state. NOTHING here reads, writes or deletes `users`.
//
// ── DOC ID = THE LOWERCASED EMAIL ──
// Deterministic, so one person cannot be added twice, and a record can be
// written or removed without searching for it first.
//
// ── WHAT IS STORED ──
//   email               lowercased, also the doc id
//   role                'admin' | 'shop'
//   canManageCreditHold boolean capability, NOT a third role
//   addedBy/addedAt, updatedBy/updatedAt — who changed access, and when
//
// ⚠️ NO displayName. It stays derived from the email prefix (see
// teamMembers.js): it is the identity written into every document's
// salesperson, every comment's author and every notification's recipient, and a
// stored second copy is how those get stranded.
//
// ⚠️ UI-LEVEL ONLY. The repo carries no Firestore rules, so any authenticated
// session can write this collection directly. These guards stop a mistake, not
// an attacker.
// ============================================================

import { collection, doc, getDocs, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeEmail, ROLES } from '../data/teamMembers';

export const TEAM_ROLES_COLLECTION = 'teamRoles';

/**
 * Every role record.
 *
 * NEVER THROWS. A failure comes back as { ok: false, records: [] }, because the
 * caller's correct response to "the read failed" and to "there are no records"
 * is the same: fall back to the file. Throwing would invite a catch that
 * resolves to the restricted role, which is the lockout this system exists to
 * avoid.
 */
export async function fetchTeamRoles() {
  try {
    const snap = await getDocs(collection(db, TEAM_ROLES_COLLECTION));
    const records = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(r => normalizeEmail(r.email) && ROLES.includes(r.role));
    return { ok: true, records };
  } catch (err) {
    console.error(`[teamRoles] Could not read ${TEAM_ROLES_COLLECTION} — falling back to the file:`, err);
    return { ok: false, records: [], error: err };
  }
}

/** Add someone, or overwrite their record outright. */
export async function saveTeamMember({ email, role, canManageCreditHold = false }, byName) {
  const address = normalizeEmail(email);
  if (!address) throw new Error('An email address is required.');
  if (!ROLES.includes(role)) throw new Error(`"${role}" is not a role.`);
  const now = new Date().toISOString();
  await setDoc(doc(db, TEAM_ROLES_COLLECTION, address), {
    email: address,
    role,
    canManageCreditHold: !!canManageCreditHold,
    addedBy: byName || '',
    addedAt: now,
    updatedBy: byName || '',
    updatedAt: now,
  }, { merge: true });
}

/** Change one field on an existing record, keeping the audit stamp current. */
export async function patchTeamMember(email, fields, byName) {
  const address = normalizeEmail(email);
  if (!address) throw new Error('An email address is required.');
  if (fields.role !== undefined && !ROLES.includes(fields.role)) {
    throw new Error(`"${fields.role}" is not a role.`);
  }
  await updateDoc(doc(db, TEAM_ROLES_COLLECTION, address), {
    ...fields,
    updatedBy: byName || '',
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Drop someone's record.
 *
 * For one of the file's five accounts this removes the OVERRIDE only — they
 * fall back to their file role, which is deliberate and is stated on screen.
 */
export async function removeTeamMember(email) {
  const address = normalizeEmail(email);
  if (!address) return;
  await deleteDoc(doc(db, TEAM_ROLES_COLLECTION, address));
}
